/* eslint-disable no-console */
/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { Form } from "semantic-ui-react";
import { useFormik } from "formik";
import { ClassSchedule, CourseTeacher, Room } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./ClassScheduleForm.form";
import { withEmptyOption } from "../../../../utils";

const classScheduleController = new ClassSchedule();
const courseTeacherController = new CourseTeacher();
const roomController = new Room();

export function ClassScheduleForm(props) {
  const {
    onClose,
    onReload,
    classSchedule,
    gradeId,
    academicCycleId,
    initialDay,
    initialTime,
    initialEndTime,
  } = props;
  const { accessToken } = useAuth();

  const [courseTeachersOptions, setCourseTeachersOptions] = useState([]);
  const [roomsOptions, setRoomsOptions] = useState([]);

  const daysOfWeekOptions = [
    { value: "Lunes", text: "Lunes" },
    { value: "Martes", text: "Martes" },
    { value: "Miércoles", text: "Miércoles" },
    { value: "Jueves", text: "Jueves" },
    { value: "Viernes", text: "Viernes" },
    { value: "Sábado", text: "Sábado" },
    { value: "Domingo", text: "Domingo" },
  ];

  const formik = useFormik({
    initialValues: initialValues(classSchedule, initialDay, initialTime, initialEndTime),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = {
          courseTeacherId: formValue.courseTeacherId,
          gradeId: gradeId || formValue.gradeId,
          academicCycleId: academicCycleId || formValue.academicCycleId,
          classroom: formValue.classroom,
          scheduledDay: formValue.scheduledDay,
          startTime: `${formValue.startTime}:00`, // Convert HH:MM to HH:MM:SS
          endTime: `${formValue.endTime}:00`, // Convert HH:MM to HH:MM:SS
          notes: formValue.notes,
        };

        if (classSchedule) {
          await classScheduleController.updateClassSchedule(
            accessToken,
            classSchedule.id,
            data
          );
        } else {
          await classScheduleController.createClassSchedule(accessToken, data);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    // Set gradeId and academicCycleId when component mounts
    if (gradeId) {
      formik.setFieldValue("gradeId", gradeId);
    }
    if (academicCycleId) {
      formik.setFieldValue("academicCycleId", academicCycleId);
    }
  }, [gradeId, academicCycleId]);

  useEffect(() => {
    (async () => {
      try {
        // Load course teachers (teacher-subject assignments) for the grade
        if (gradeId && academicCycleId) {
          const courseTeachersResponse =
            await courseTeacherController.getCourseTeachersByAcademicCycle(
              accessToken,
              academicCycleId,
              gradeId
            );
          const courseTeachersOptions =
            courseTeachersResponse.assignedSubjects.map((ct) => ({
              value: ct.id,
              text: `${ct.Subject?.name} - ${ct.User?.firstname} ${
                ct.User?.lastname || ""
              } ${ct.User?.secondLastname || ""}`.trim(),
            }));
          setCourseTeachersOptions(courseTeachersOptions);
        }

        // Load rooms
        const roomsResponse = await roomController.getRooms(accessToken);
        const roomsOptions = roomsResponse.map((room) => ({
          value: room.id,
          text: room.name,
        }));
        setRoomsOptions(roomsOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [accessToken, gradeId, academicCycleId]);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Group widths="equal">
        <Form.Select
          className="edunova-select"
          name="scheduledDay"
          label="Día de la semana"
          placeholder="Selecciona un día de la semana"
          clearable
          options={withEmptyOption(daysOfWeekOptions, "Selecciona un día de la semana")}
          onChange={(e, { value }) =>
            formik.setFieldValue("scheduledDay", value)
          }
          value={formik.values.scheduledDay}
          error={formik.errors.scheduledDay}
        />
        <Form.Select
          className="edunova-select"
          name="courseTeacherId"
          label="Asignatura - Profesor"
          placeholder="Selecciona una asignatura y profesor"
          clearable
          options={withEmptyOption(courseTeachersOptions, "Selecciona una asignatura y profesor")}
          onChange={(e, { value }) =>
            formik.setFieldValue("courseTeacherId", value)
          }
          value={formik.values.courseTeacherId}
          error={formik.errors.courseTeacherId}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          name="startTime"
          label="Hora de inicio (HH:MM)"
          type="time"
          onChange={formik.handleChange}
          value={formik.values.startTime}
          error={formik.errors.startTime}
        />
        <Form.Input
          name="endTime"
          label="Hora de fin (HH:MM)"
          type="time"
          onChange={formik.handleChange}
          value={formik.values.endTime}
          error={formik.errors.endTime}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          name="classroom"
          label="Aula"
          onChange={formik.handleChange}
          value={formik.values.classroom}
          error={formik.errors.classroom}
        />
        <Form.TextArea
          name="notes"
          label="Notas/Comentarios"
          onChange={formik.handleChange}
          value={formik.values.notes}
          error={formik.errors.notes}
          rows={3}
        />
      </Form.Group>

      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {classSchedule ? "Actualizar horario" : "Crear horario"}
      </Form.Button>
    </Form>
  );
}
